import React from "react";
import { useNavigate } from "react-router-dom";
import useCanonicalUrl from "../hooks/useCanonicalUrl";
import styles from "./HomePage.module.css";

const HomePage = () => {
  const navigate = useNavigate();
  useCanonicalUrl("/");

  return (
    <div className={styles.homeContainer}>
      {/* Hero */}
      <header className={styles.hero}>
        <div>
          <p className={styles.kicker}>
            Hindi Reading &amp; Writing Practice · Singapore
          </p>
          <h1 className={styles.heroTitle}>
            AI-Powered{" "}
            <span className={styles.heroHighlight}>
              Hindi Reading &amp; Writing
            </span>{" "}
            Practice for Primary and Secondary Students in Singapore
          </h1>
          <p className={styles.heroSubtitle}>
            Instant AI feedback on Hindi reading, writing, and pronunciation —
            built for Primary and Secondary students, parents, and teachers in
            Singapore.
          </p>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={() => navigate("/login")}
          >
            Sign In to Get Started
          </button>
          <p className={styles.heroNote}>
            Sign in with Google or Facebook — no setup needed.
          </p>
        </div>

        <div className={styles.browserMock}>
          <div className={styles.browserHeader}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.browserUrl}>
              https://jigyasaonline.com/reading
            </span>
          </div>
          <div className={styles.browserBody}>
            <div className={styles.metaRow}>
              <span className={`${styles.chip} ${styles.hindiText}`}>
                पठन अभ्यास · Reading
              </span>
              <span className={`${styles.chip} ${styles.chipNeutral}`}>
                Primary 2
              </span>
            </div>
            <p className={`${styles.passageText} ${styles.hindiText}`}>
              <span className={styles.readWord}>गीता</span>{" "}
              <span className={`${styles.readWord} ${styles.wordDelay1}`}>
                हर
              </span>{" "}
              <span className={`${styles.readWord} ${styles.wordDelay2}`}>
                सुबह
              </span>{" "}
              <span className={styles.readWordFlag}>विद्यालय</span>{" "}
              <span className={`${styles.readWord} ${styles.wordDelay4}`}>
                जाती
              </span>{" "}
              <span className={`${styles.readWord} ${styles.wordDelay5}`}>
                है।
              </span>
              <br />
              <span className={styles.dimText}>
                वह पुस्तकालय में कहानी पढ़ती है।
              </span>
            </p>
            <div className={styles.micRow}>
              <span className={styles.micDot}>
                <span className={styles.micRing} />
                <span className={styles.micGlyph} />
              </span>
              <span className={styles.waveform}>
                <span className={styles.waveBar} />
                <span className={`${styles.waveBar} ${styles.waveDelay1}`} />
                <span className={`${styles.waveBar} ${styles.waveDelay2}`} />
                <span className={`${styles.waveBar} ${styles.waveDelay3}`} />
                <span className={`${styles.waveBar} ${styles.waveDelay4}`} />
              </span>
              <span className={`${styles.micLabel} ${styles.hindiText}`}>
                सुन रहा है… listening
              </span>
            </div>
            <div className={styles.statsRow}>
              <div>
                <p className={styles.statLabel}>Accuracy</p>
                <div className={styles.statNumbers}>
                  <span className={styles.numBefore}>86%</span>
                  <span className={styles.numAfter}>
                    92%<span className={styles.numDelta}>▲6</span>
                  </span>
                </div>
              </div>
              <div>
                <p className={styles.statLabel}>This week</p>
                <div className={styles.sparkline}>
                  <span className={`${styles.sparkBar} ${styles.sparkH1}`} />
                  <span className={`${styles.sparkBar} ${styles.sparkH2}`} />
                  <span className={`${styles.sparkBar} ${styles.sparkH3}`} />
                  <span className={`${styles.sparkBar} ${styles.sparkH4}`} />
                  <span className={`${styles.sparkBar} ${styles.sparkH5}`} />
                  <span
                    className={`${styles.sparkBar} ${styles.sparkActive} ${styles.sparkH6}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Writing comprehension */}
      <section className={`${styles.featureRow} ${styles.featureReverse}`}>
        <div className={styles.featureText}>
          <p className={styles.kicker}>Hindi Writing Comprehension</p>
          <h2 className={styles.featureTitle}>
            Hindi writing comprehension, graded by AI in seconds
          </h2>
          <p className={styles.featureDesc}>
            Jigyasa AI grades Hindi writing comprehension answers for Primary
            and Secondary students in Singapore the moment they're submitted.
            Each answer is checked against the passage and the question, with
            clear feedback on what to improve and marks awarded automatically
            — every attempt saved for later review.
          </p>
          <button
            type="button"
            className={styles.featureCtaButton}
            onClick={() => navigate("/login")}
          >
            Try Writing Practice Free
          </button>
        </div>

        <div className={styles.browserMock}>
          <div className={styles.browserHeader}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.browserUrl}>
              https://jigyasaonline.com/writing
            </span>
          </div>
          <div className={styles.browserBody}>
            <p className={`${styles.questionTag} ${styles.hindiText}`}>
              प्रश्न 2 · Writing · Secondary 4
            </p>
            <p className={`${styles.passageMini} ${styles.hindiText}`}>
              हालांकि आम लोगों को ये पुराने खंडहर पूरी तरह वीरान लग सकते थे,
              लेकिन पत्थर की सीढ़ियों पर जमी काई में हुई हल्की-सी छेड़छाड़ से
              पता चलता था कि हाल ही में कोई वहां आया था। पुरातत्वविदों के बीच
              इस बात को लेकर गहरा मतभेद था कि क्या यह सबूत उनकी रिसर्च में
              किसी बड़ी कामयाबी की ओर इशारा करता है या फिर यह कोई बहुत
              चालाकी से रचा गया धोखा है।
            </p>
            <p className={`${styles.questionLine} ${styles.hindiText}`}>
              किस भौतिक सबूत से यह साबित हुआ कि हाल ही में कोई उन प्राचीन
              खंडहरों में गया था?
            </p>
            <div className={styles.answerBox}>
              <p className={styles.answerLabel}>उत्तर · your answer</p>
              <span className={`${styles.typedWord} ${styles.typeDelay1} ${styles.hindiText}`}>
                पत्थर की सीढ़ियों पर
              </span>{" "}
              <span className={`${styles.typedWord} ${styles.typeDelay2} ${styles.hindiText}`}>
                काई के उखड़े या अस्त-व्यस्त होने से
              </span>{" "}
              <span className={`${styles.typedWord} ${styles.typeDelay3} ${styles.hindiText}`}>
                पता चलता है
              </span>{" "}
              <span className={`${styles.typedWord} ${styles.typeDelay4} ${styles.hindiText}`}>
                कि हाल ही में
              </span>{" "}
              <span className={`${styles.typedWord} ${styles.typeDelay5} ${styles.hindiText}`}>
                कोई उन प्राचीन खंडहरों में
              </span>{" "}
              <span className={`${styles.typedWord} ${styles.typeDelay6} ${styles.hindiText}`}>
                आया था।
              </span>
            </div>
            <div className={styles.submitRow}>
              <span className={`${styles.submitButton} ${styles.hindiText}`}>
                जाँचें · Check
              </span>
            </div>
            <div className={styles.feedbackCard}>
              <span className={styles.checkCircle}>✓</span>
              <div>
                <div className={styles.feedbackHead}>
                  <span className={`${styles.feedbackTitle} ${styles.hindiText}`}>
                    सही उत्तर · Correct
                  </span>
                  <span className={styles.marksBadge}>2/2 marks</span>
                </div>
                <p className={`${styles.feedbackDesc} ${styles.hindiText}`}>
                  पंक्ति 2 से मेल खाता है — full sentence with the correct verb
                  form.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teacher dashboard */}
      <section className={styles.featureRow}>
        <div className={styles.featureText}>
          <p className={styles.kicker}>For Hindi Teachers in Singapore</p>
          <h2 className={styles.featureTitle}>
            A Hindi teacher dashboard for Singapore schools
          </h2>
          <p className={styles.featureDesc}>
            Jigyasa AI gives Hindi teachers in Singapore one dashboard to
            create reading and writing comprehensions and track every
            student's progress — reading scores, every writing attempt with
            AI feedback, and space to add your own comments on any answer.
          </p>
          <button
            type="button"
            className={styles.featureCtaButton}
            onClick={() => navigate("/login")}
          >
            Sign In as a Teacher
          </button>
        </div>

        <div className={styles.browserMock}>
          <div className={styles.browserHeader}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.browserUrl}>
              https://jigyasaonline.com/teachers
            </span>
          </div>
          <div className={styles.browserBody}>
            <div className={styles.tableHead}>
              <span className={styles.tableTitle}>Class P5 · Reading &amp; Writing</span>
              <span className={`${styles.chip} ${styles.chipNeutral}`}>
                18 students
              </span>
            </div>
            <div className={styles.tableRowHead}>
              <span>Student</span>
              <span>Attempts</span>
              <span>Progress</span>
              <span>Status</span>
            </div>
            <div className={styles.tableRow}>
              <span className={styles.studentName}>
                <span className={styles.avatar}>AK</span>Aarav K.
              </span>
              <span className={styles.attemptsCol}>12</span>
              <span className={styles.progressBar}>
                <span className={`${styles.progressFill} ${styles.progressW82}`} />
              </span>
              <span className={styles.statusChip}>On track</span>
            </div>
            <div className={`${styles.tableRow} ${styles.tableRowHighlight}`}>
              <span className={styles.studentName}>
                <span className={styles.avatar}>DS</span>Diya S.
              </span>
              <span className={styles.attemptsCol}>9</span>
              <span className={styles.progressBar}>
                <span className={`${styles.progressFill} ${styles.progressW64}`} />
              </span>
              <span className={`${styles.statusChip} ${styles.statusWarn}`}>
                Review
              </span>
            </div>
            <div className={styles.tableRow}>
              <span className={styles.studentName}>
                <span className={styles.avatar}>RN</span>Rohan N.
              </span>
              <span className={styles.attemptsCol}>11</span>
              <span className={styles.progressBar}>
                <span className={`${styles.progressFill} ${styles.progressW71}`} />
              </span>
              <span className={styles.statusChip}>On track</span>
            </div>
            <div className={styles.tableRow}>
              <span className={styles.studentName}>
                <span className={styles.avatar}>IM</span>Ishita M.
              </span>
              <span className={styles.attemptsCol}>6</span>
              <span className={styles.progressBar}>
                <span className={`${styles.progressFill} ${styles.progressW45}`} />
              </span>
              <span className={`${styles.statusChip} ${styles.statusWarn}`}>
                Review
              </span>
            </div>
            <div className={styles.teacherFeedback}>
              <div className={styles.teacherFeedbackHead}>
                <span className={styles.teacherAvatar}>T</span>
                <span className={styles.teacherFeedbackTag}>
                  Your feedback · Diya S. · Q2
                </span>
              </div>
              <p className={`${styles.teacherFeedbackLine} ${styles.hindiText}`}>
                <span className={`${styles.typedWord} ${styles.typeDelay2}`}>
                  अच्छा
                </span>{" "}
                <span className={`${styles.typedWord} ${styles.typeDelay3}`}>
                  प्रयास
                </span>{" "}
                <span className={`${styles.typedWord} ${styles.typeDelay4}`}>
                  —
                </span>{" "}
                <span className={`${styles.typedWord} ${styles.typeDelay5}`}>
                  re-read
                </span>{" "}
                <span className={`${styles.typedWord} ${styles.typeDelay6}`}>
                  line 2 before answering.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
